
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Profile } from "@/hooks/profile/types";
import { toast } from "@/hooks/use-toast";
import { useCompleteProfileForm } from "./hooks/useCompleteProfileForm";
import { useProfileFieldValidation } from "./hooks/useProfileFieldValidation";
import { useCompleteProfileModal } from "./hooks/useCompleteProfileModal";
import { CompleteProfileModalHeader } from "./components/CompleteProfileModalHeader";
import { CompleteProfileFormFields } from "./components/CompleteProfileFormFields";
import { CompleteProfileFormActions } from "./components/CompleteProfileFormActions";

interface CompleteProfileModalProps {
  profile: Profile;
  onComplete: (updates: Partial<Profile>) => Promise<void>;
  forceOpen?: boolean;
}

export const CompleteProfileModal = ({ profile, onComplete, forceOpen = false }: CompleteProfileModalProps) => {
  const { open, setOpen } = useCompleteProfileModal({ profile, forceOpen });
  
  const {
    firstName, setFirstName,
    lastName, setLastName,
    birthdate, setBirthdate,
    phone, setPhone,
    address, setAddress,
    emergencyPhone, setEmergencyPhone,
    healthInsurance, setHealthInsurance,
    isSubmitting, setIsSubmitting,
    isComplete
  } = useCompleteProfileForm({ profile });

  const {
    fieldErrors,
    hasErrors,
    setHasErrors,
    validateField,
    validateAllFields
  } = useProfileFieldValidation();

  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveErrorMessage(null);

    if (!validateAllFields(firstName, lastName, birthdate)) {
      toast({
        variant: "destructive",
        title: "Campos incompletos",
        description: "Por favor, completa todos los campos obligatorios.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setHasErrors(false);
      
      const updates = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        birthdate: birthdate.trim(),
        phone: phone.trim() || null,
        address: address.trim() || null,
        emergency_phone: emergencyPhone.trim() || null,
        health_insurance: healthInsurance.trim() || null,
      };
      
      console.log("Saving profile data:", updates);
      await onComplete(updates);
      
      toast({
        title: "Perfil actualizado",
        description: "Tus datos han sido guardados correctamente.",
      });
      
      sessionStorage.setItem("profileCompleted", "true");
      
      if (!forceOpen) {
        setOpen(false);
      }
    } catch (error: any) {
      console.error("Error al guardar el perfil:", error);
      const message = error?.message || "No se pudieron guardar tus datos. Por favor, intenta de nuevo.";
      setSaveErrorMessage(message);
      toast({
        variant: "destructive",
        title: "Error al guardar",
        description: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={forceOpen ? undefined : setOpen}
      modal={true}
    >
      <DialogContent 
        className="max-w-[95vw] sm:max-w-xs max-h-[90vh] overflow-y-auto rounded-xl bg-[#293233] border-2 border-[#2196A3]"
        showCloseButton={!forceOpen}
        onInteractOutside={forceOpen ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={forceOpen ? (e) => e.preventDefault() : undefined}
      >
        <CompleteProfileModalHeader forceOpen={forceOpen} />
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <CompleteProfileFormFields
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            birthdate={birthdate}
            setBirthdate={setBirthdate}
            phone={phone}
            setPhone={setPhone}
            address={address}
            setAddress={setAddress}
            emergencyPhone={emergencyPhone}
            setEmergencyPhone={setEmergencyPhone}
            healthInsurance={healthInsurance}
            setHealthInsurance={setHealthInsurance}
            fieldErrors={fieldErrors}
            validateField={validateField}
          />
          
          <CompleteProfileFormActions
            hasErrors={hasErrors}
            saveErrorMessage={saveErrorMessage}
            isComplete={isComplete}
            isSubmitting={isSubmitting}
            forceOpen={forceOpen}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};
